import { useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Form, Formik, FormikHelpers, useField } from "formik";
import { useQuery, useQueryClient } from "react-query";
import UploadImageFormik from "components/UploadImageFormik";
import useLocalStorage from "helpers/useLocalStorage";
import { errorAdd, successAdd } from "helpers/sweetalert";
import {
  getUserProfileData,
  UserKycPayload,
  userGetMe,
  userKycGetMe,
  userKycGetStatus,
  userKycSubmit,
  userKycUpdate,
} from "services/user.service";
import type { IconType } from "react-icons";
import {
  FiAlertTriangle,
  FiBookOpen,
  FiBriefcase,
  FiCalendar,
  FiCheck,
  FiCreditCard,
  FiFileText,
  FiShield,
} from "react-icons/fi";

type DocumentType = "identity-card" | "family-book" | "passport" | "driver";

type DocumentOption = {
  id: DocumentType;
  label: string;
  icon: IconType;
  identityLabel: string;
  identityPlaceholder: string;
  birthLabel: string;
  attachmentLabels: [string, string, string];
};

type KycStatusInfo = {
  label: string;
  badgeClassName: string;
};

type KycFormValues = UserKycPayload & {
  lastName: string;
  district: string;
  province: string;
};

const documentOptions: DocumentOption[] = [
  {
    id: "identity-card",
    label: "ບັດປະຈຳຕົວ",
    icon: FiCreditCard,
    identityLabel: "ເລກບັດປະຈຳຕົວ",
    identityPlaceholder: "01-XXXX-XXXX",
    birthLabel: "ວັນເດືອນປີເກີດ",
    attachmentLabels: [
      "ບັດປະຈຳຕົວ (ດ້ານໜ້າ)",
      "ບັດປະຈຳຕົວ (ດ້ານຫຼັງ)",
      "ເຈົ້າຖືບັດປະຈຳຕົວ",
    ],
  },
  {
    id: "family-book",
    label: "ພາສະປອດ",
    icon: FiBookOpen,
    identityLabel: "ເລກພາສະປອດ",
    identityPlaceholder: "LP1234567",
    birthLabel: "ວັນເດືອນປີເກີດ (ຕາມພາສະປອດ)",
    attachmentLabels: [
      "ພາສະປອດ (ດ້ານໜ້າ)",
      "ພາສະປອດ (ໜ້າທີ 2/3)",
      "ເຈົ້າຖືພາສະປອດ",
    ],
  },
  {
    id: "passport",
    label: "ໃບຂັບຂີ່",
    icon: FiBriefcase,
    identityLabel: "ເລກໃບຂັບຂີ່",
    identityPlaceholder: "ເລກໃບຂັບຂີ່",
    birthLabel: "ວັນເດືອນປີເກີດ (ຕາມໃບຂັບຂີ່)",
    attachmentLabels: [
      "ໃບຂັບຂີ່ (ດ້ານໜ້າ)",
      "ໃບຂັບຂີ່ (ດ້ານຫຼັງ)",
      "ເຈົ້າຖືໃບຂັບຂີ່",
    ],
  },
  {
    id: "driver",
    label: "ສຳມະໂນຄົວ",
    icon: FiFileText,
    identityLabel: "ເລກສຳມະໂນຄົວ",
    identityPlaceholder: "ເລກສຳມະໂນຄົວ",
    birthLabel: "ວັນເດືອນປີເກີດ",
    attachmentLabels: [
      "ສຳມະໂນຄົວ (ດ້ານໜ້າ)",
      "ສຳມະໂນຄົວ (ດ້ານຫຼັງ)",
      "ເຈົ້າຖືສຳມະໂນຄົວ",
    ],
  },
];

const statusMap: Record<string, KycStatusInfo> = {
  draft: {
    label: "ຍັງບໍ່ໄດ້ສົ່ງ",
    badgeClassName: "bg-slate-100 text-slate-600",
  },
  pending: {
    label: "ລໍຖ້າກວດສອບ",
    badgeClassName: "bg-amber-100 text-amber-700",
  },
  approved: {
    label: "ອະນຸມັດແລ້ວ",
    badgeClassName: "bg-emerald-100 text-emerald-700",
  },
  rejected: {
    label: "ຖືກປະຕິເສດ",
    badgeClassName: "bg-red-100 text-red-700",
  },
};

const defaultKycValues: UserKycPayload = {
  fullName: "",
  dateOfBirth: "",
  address: "",
  identityNo: "",
  documentFrontImage: "",
  documentBackImage: "",
  selfieWithDocumentImage: "",
  bankAccountNo: "",
  bankAccountName: "",
  bankName: "",
};

const defaultKycFormValues: KycFormValues = {
  ...defaultKycValues,
  lastName: "",
  district: "",
  province: "",
};

const uploadTileStyle: CSSProperties = {
  width: "100%",
  height: "auto",
  minHeight: 128,
  aspectRatio: "16 / 9",
  borderRadius: 8,
  border: "1px dashed #cbd5e1",
  backgroundColor: "#f1f5f9",
};

function splitFullName(fullName: string) {
  const nameParts = fullName.trim().split(/\s+/).filter(Boolean);

  if (nameParts.length <= 1) {
    return {
      firstName: fullName,
      lastName: "",
    };
  }

  return {
    firstName: nameParts[0],
    lastName: nameParts.slice(1).join(" "),
  };
}

function composeKycPayload(values: KycFormValues): UserKycPayload {
  const fullName = [values.fullName, values.lastName]
    .map((value) => value.trim())
    .filter(Boolean)
    .join(" ");
  const address = [values.address, values.district, values.province]
    .map((value) => value.trim())
    .filter(Boolean)
    .join(", ");

  return {
    fullName,
    dateOfBirth: values.dateOfBirth,
    address,
    identityNo: values.identityNo,
    documentFrontImage: values.documentFrontImage,
    documentBackImage: values.documentBackImage,
    selfieWithDocumentImage: values.selfieWithDocumentImage,
    bankAccountNo: values.bankAccountNo,
    bankAccountName: values.bankAccountName,
    bankName: values.bankName,
  };
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function FieldError({ name }: { name: keyof KycFormValues }) {
  const [, meta] = useField<string>(name);

  if (!meta.touched || !meta.error) return null;

  return <div className="mt-1 text-[11px] text-red-500">{meta.error}</div>;
}

function TextField({
  label,
  name,
  placeholder,
  required,
  type = "text",
  rows,
  full,
  icon: Icon,
}: {
  label: string;
  name: keyof KycFormValues;
  placeholder?: string;
  required?: boolean;
  type?: string;
  rows?: number;
  full?: boolean;
  icon?: IconType;
}) {
  const [field, meta] = useField<string>(name);
  const hasError = Boolean(meta.touched && meta.error);
  const inputClassName = cx(
    "h-[42px] w-full rounded-lg border bg-[#f1f5f9] px-4 text-[13px] text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-primary focus:bg-white focus:ring-3 focus:ring-primary/10",
    Icon ? "pr-10" : "",
    hasError ? "border-red-300" : "border-[#dbe3ec]",
  );

  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label
        htmlFor={name}
        className="mb-2 block text-[12px] font-medium text-slate-600"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        {rows ? (
          <textarea
            {...field}
            id={name}
            rows={rows}
            placeholder={placeholder}
            className={cx(inputClassName, "min-h-[42px] resize-none py-3")}
          />
        ) : (
          <input
            {...field}
            id={name}
            type={type}
            placeholder={placeholder}
            className={inputClassName}
          />
        )}
        {Icon && (
          <Icon className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
        )}
      </div>
      <FieldError name={name} />
    </div>
  );
}

function UploadField({
  label,
  name,
}: {
  label: string;
  name: keyof KycFormValues;
}) {
  return (
    <div>
      <div className="mb-2 text-[12px] font-medium text-slate-600">{label}</div>
      <UploadImageFormik
        name={name}
        rootStyle={{ alignItems: "stretch" }}
        style={uploadTileStyle}
        commentText="ແນບໄຟລ໌ຮູບພາບ · JPG ຫຼື PNG"
      />
    </div>
  );
}

function KycHeroCard({ statusInfo }: { statusInfo: KycStatusInfo }) {
  return (
    <div className="rounded-lg border border-primary bg-white p-4 shadow-[0_10px_28px_rgba(22,143,235,0.08)] sm:px-5">
      <div className="flex items-start gap-3 sm:items-center sm:gap-4">
        <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
          <FiShield className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="m-0 text-[15px] font-bold text-slate-900">
              ຢືນຢັນໂຕຕົນ (KYC)
            </h2>
            <span
              className={cx(
                "rounded-full px-2 py-0.5 text-[11px] font-bold",
                statusInfo.badgeClassName,
              )}
            >
              {statusInfo.label}
            </span>
          </div>
          <p className="m-0 mt-1 text-[12px] leading-5 text-slate-500">
            ທ່ານສາມາດໃຊ້ບໍລິການໄດ້ຫຼັງຈາກຢືນຢັນ KYC ແລ້ວ.
          </p>
        </div>
      </div>
    </div>
  );
}

function KycApprovedCard({
  fullName,
  username,
  email,
  phone,
  dateOfBirth,
  address,
  identityNo,
  documentFrontImage,
  documentBackImage,
  selfieWithDocumentImage,
  bankAccountName,
  bankAccountNo,
  bankName,
}: {
  fullName: string;
  username: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  identityNo: string;
  documentFrontImage: string;
  documentBackImage: string;
  selfieWithDocumentImage: string;
  bankAccountName: string;
  bankAccountNo: string;
  bankName: string;
}) {
  const profileItems = [
    { label: "ຊື່-ນາມສະກຸນ", value: fullName },
    { label: "Username", value: username },
    { label: "Email", value: email },
    { label: "ເບີໂທ", value: phone },
    { label: "ວັນເດືອນປີເກີດ", value: dateOfBirth },
    { label: "ເລກເອກະສານ", value: identityNo },
    { label: "ທີ່ຢູ່", value: address },
  ];
  const bankItems = [
    { label: "ຊື່ບັນຊີ", value: bankAccountName },
    { label: "ເລກບັນຊີ", value: bankAccountNo },
    { label: "ທະນາຄານ", value: bankName },
  ];
  const documentItems = [
    { label: "ເອກະສານດ້ານໜ້າ", value: documentFrontImage },
    { label: "ເອກະສານດ້ານຫຼັງ", value: documentBackImage },
    { label: "ຮູບຖືເອກະສານ", value: selfieWithDocumentImage },
  ];

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-emerald-200 bg-white p-4 shadow-sm sm:p-5 md:p-6">
        <div className="flex items-start gap-3 sm:items-center sm:gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
            <FiCheck className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="m-0 text-[16px] font-bold text-slate-900">
                ຢືນຢັນ KYC ແລ້ວ
              </h2>
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                KYC ແລ້ວ
              </span>
            </div>
            <p className="m-0 mt-1 text-[12px] leading-5 text-slate-500">
              ບັນຊີນີ້ຜ່ານການຢືນຢັນໂຕຕົນແລ້ວ.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <div className="rounded-lg border border-[#dbe3ec] bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-3 text-[14px] font-bold text-slate-900">
            ຂໍ້ມູນໂປຣໄຟລ໌
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {profileItems.map((item) => (
              <div
                key={item.label}
                className={item.label === "ທີ່ຢູ່" ? "sm:col-span-2" : ""}
              >
                <div className="text-[12px] text-slate-500">{item.label}</div>
                <div className="mt-1 break-words text-[14px] font-semibold text-slate-800">
                  {item.value || "-"}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-[#dbe3ec] bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-3 text-[14px] font-bold text-slate-900">
            ບັນຊີຮັບເງິນ
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {bankItems.map((item) => (
              <div key={item.label}>
                <div className="text-[12px] text-slate-500">{item.label}</div>
                <div className="mt-1 break-words text-[14px] font-semibold text-slate-800">
                  {item.value || "-"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DocumentTabs({
  activeDocument,
  onChange,
}: {
  activeDocument: DocumentOption;
  onChange: (documentType: DocumentType) => void;
}) {
  return (
    <div>
      <div className="mb-3 text-[12px] font-medium text-slate-600">
        ເລືອກປະເພດເອກະສານ
      </div>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3">
        {documentOptions.map((option) => {
          const Icon = option.icon;
          const isActive = activeDocument.id === option.id;

          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(option.id)}
              className={cx(
                "inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg border px-2 text-[12px] font-bold transition sm:w-auto sm:px-3",
                isActive
                  ? "border-primary bg-[#eef7ff] text-primary shadow-[0_8px_20px_rgba(22,143,235,0.12)]"
                  : "border-[#dbe3ec] bg-white text-slate-600 hover:border-primary hover:text-primary",
              )}
            >
              <Icon className="h-4 w-4" />
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function KycFormCard({
  activeDocument,
  initialValues,
  isReady,
  isExistingKyc,
  onDocumentChange,
  onSubmit,
}: {
  activeDocument: DocumentOption;
  initialValues: KycFormValues;
  isReady: boolean;
  isExistingKyc: boolean;
  onDocumentChange: (documentType: DocumentType) => void;
  onSubmit: (
    values: KycFormValues,
    helpers: FormikHelpers<KycFormValues>,
  ) => Promise<void>;
}) {
  return (
    <div className="rounded-lg border border-[#dbe3ec] bg-white p-4 shadow-sm sm:p-5 md:p-6">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={(values) => {
          const errors: Partial<Record<keyof KycFormValues, string>> = {};

          if (!values.fullName.trim()) errors.fullName = "Required";
          if (!values.lastName.trim()) errors.lastName = "Required";
          if (!values.identityNo.trim()) errors.identityNo = "Required";
          if (!values.dateOfBirth.trim()) errors.dateOfBirth = "Required";
          if (!values.address.trim()) errors.address = "Required";
          if (!values.district.trim()) errors.district = "Required";
          if (!values.province.trim()) errors.province = "Required";
          if (!values.bankAccountNo.trim()) {
            errors.bankAccountNo = "Required";
          }
          if (!values.bankAccountName.trim()) {
            errors.bankAccountName = "Required";
          }
          if (!values.bankName.trim()) errors.bankName = "Required";
          if (!values.documentFrontImage) {
            errors.documentFrontImage = "Required";
          }
          if (!values.documentBackImage) {
            errors.documentBackImage = "Required";
          }
          if (!values.selfieWithDocumentImage) {
            errors.selfieWithDocumentImage = "Required";
          }

          return errors;
        }}
      >
        {({ isSubmitting }) => (
          <Form className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextField
                label="ຊື່"
                name="fullName"
                required
                placeholder="ປ້ອນຊື່"
              />
              <TextField
                label="ນາມສະກຸນ"
                name="lastName"
                required
                placeholder="ປ້ອນນາມສະກຸນ"
              />

              <DocumentTabs
                activeDocument={activeDocument}
                onChange={onDocumentChange}
              />
              <TextField
                label={activeDocument.identityLabel}
                name="identityNo"
                required
                placeholder={activeDocument.identityPlaceholder}
              />
              <TextField
                label={activeDocument.birthLabel}
                name="dateOfBirth"
                type="date"
                required
                icon={FiCalendar}
              />
              <TextField
                label="ທີ່ຢູ່ (ປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ)"
                name="address"
                required
                placeholder="ບ້ານ, ເມືອງ, ແຂວງ"
                full
              />
              <TextField
                label="ເມືອງ"
                name="district"
                required
                placeholder="ປ້ອນເມືອງ"
              />
              <TextField
                label="ແຂວງ"
                name="province"
                required
                placeholder="ປ້ອນແຂວງ"
              />
            </div>

            <div>
              <div className="mb-3 text-[12px] font-medium text-slate-600">
                ບັນຊີຮັບເງິນ
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <TextField
                  label="ຊື່ບັນຊີ"
                  name="bankAccountName"
                  required
                  placeholder="ປ້ອນຊື່ບັນຊີ"
                />
                <TextField
                  label="ເລກບັນຊີທະນາຄານ"
                  name="bankAccountNo"
                  required
                  placeholder="ປ້ອນເລກບັນຊີ"
                />
                <TextField
                  label="ຊື່ທະນາຄານ"
                  name="bankName"
                  required
                  placeholder="ປ້ອນຊື່ທະນາຄານ"
                />
              </div>
            </div>

            <div>
              <div className="mb-3 text-[12px] font-medium text-slate-600">
                ຮູບຖ່າຍເອກະສານ
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <UploadField
                  label={activeDocument.attachmentLabels[0]}
                  name="documentFrontImage"
                />
                <UploadField
                  label={activeDocument.attachmentLabels[1]}
                  name="documentBackImage"
                />
                <UploadField
                  label={activeDocument.attachmentLabels[2]}
                  name="selfieWithDocumentImage"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !isReady}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 text-[14px] font-bold text-white shadow-[0_12px_24px_rgba(22,143,235,0.24)] transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-primary/45 sm:w-auto"
            >
              <FiShield className="h-4 w-4" />
              {isSubmitting
                ? "ກຳລັງບັນທຶກ..."
                : isExistingKyc
                  ? "ອັບເດດຂໍ້ມູນ"
                  : "ຢືນຢັນໂຕຕົນ"}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default function MerchantPage() {
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");
  const queryClient = useQueryClient();
  const isReady = Boolean(token && businessId);
  const [activeDocumentType, setActiveDocumentType] =
    useState<DocumentType>("identity-card");

  const { data: kycResponse, isLoading: isLoadingKyc } = useQuery(
    ["user-kyc-me", businessId],
    () => userKycGetMe({ token: token || "", businessId: businessId || "" }),
    {
      enabled: isReady,
      retry: false,
    },
  );

  const { data: authMeResponse, isLoading: isLoadingAuthMe } = useQuery(
    ["user-auth-me", businessId],
    () => userGetMe({ token: token || "", businessId: businessId || "" }),
    {
      enabled: Boolean(token),
      retry: false,
    },
  );

  const { data: kycStatusResponse, isLoading: isLoadingKycStatus } = useQuery(
    ["user-kyc-status", businessId],
    () =>
      userKycGetStatus({ token: token || "", businessId: businessId || "" }),
    {
      enabled: isReady,
      retry: false,
    },
  );

  const kycData = (kycResponse as any)?.data || kycResponse || {};
  const authMeData = getUserProfileData(authMeResponse);
  const rawKycStatus =
    (kycStatusResponse as any)?.data?.status ||
    (typeof (kycStatusResponse as any)?.data === "string"
      ? (kycStatusResponse as any).data
      : "") ||
    (kycStatusResponse as any)?.status ||
    (kycData as any)?.status ||
    "draft";
  const kycStatus = String(rawKycStatus || "draft").toLowerCase();
  const isKycApproved = kycStatus === "approved";
  const shouldShowKycForm = !isKycApproved;
  const isLoadingInitialKycState =
    isReady && (isLoadingKyc || isLoadingAuthMe || isLoadingKycStatus);
  const statusInfo = isLoadingKyc
    ? {
        label: "ກຳລັງໂຫລດ...",
        badgeClassName: "bg-slate-100 text-slate-500",
      }
    : statusMap[kycStatus] || statusMap.draft;

  const initialValues = useMemo<KycFormValues>(() => {
    const storedFullName =
      (kycData as any)?.fullName ||
      (authMeData as any)?.fullName ||
      (authMeData as any)?.name ||
      "";
    const splitName = splitFullName(storedFullName);

    return {
      ...defaultKycFormValues,
      fullName: splitName.firstName,
      lastName: splitName.lastName,
      dateOfBirth: (kycData as any)?.dateOfBirth?.slice?.(0, 10) || "",
      address: (kycData as any)?.address || (authMeData as any)?.address || "",
      identityNo: (kycData as any)?.identityNo || "",
      documentFrontImage: (kycData as any)?.documentFrontImage || "",
      documentBackImage: (kycData as any)?.documentBackImage || "",
      selfieWithDocumentImage: (kycData as any)?.selfieWithDocumentImage || "",
      bankAccountNo: (kycData as any)?.bankAccountNo || "",
      bankAccountName: (kycData as any)?.bankAccountName || "",
      bankName: (kycData as any)?.bankName || "",
    };
  }, [authMeData, kycData]);

  const activeDocument =
    documentOptions.find((option) => option.id === activeDocumentType) ||
    documentOptions[0];

  const invalidateKycQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries(["user-kyc-me", businessId]),
      queryClient.invalidateQueries(["user-kyc-status", businessId]),
    ]);
  };

  const handleKycSubmit = async (
    values: KycFormValues,
    helpers: FormikHelpers<KycFormValues>,
  ) => {
    try {
      const request = (kycData as any)?._id ? userKycUpdate : userKycSubmit;
      await request({
        body: composeKycPayload(values),
        token: token || "",
        businessId: businessId || "",
      });
      successAdd("ສົ່ງຂໍ້ມູນ KYC ສຳເລັດ");
      await invalidateKycQueries();
    } catch (error: any) {
      errorAdd(error?.response?.data?.message || "ສົ່ງຂໍ້ມູນ KYC ບໍ່ສຳເລັດ");
    } finally {
      helpers.setSubmitting(false);
    }
  };

  if (isLoadingInitialKycState) {
    return <div className="min-h-full min-w-0 bg-[#f8fafc]" />;
  }

  return (
    <div className="min-h-full min-w-0 bg-[#f8fafc]">
      {shouldShowKycForm ? (
        <div className="w-full space-y-4">
          {!isKycApproved ? (
            <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
              <FiAlertTriangle className="h-4 w-4 text-amber-400" />
              <span>ຜູ້ໃຊ້ງານຍັງບໍ່ໄດ້ຢືນຢັນໂຕຕົນ</span>
            </div>
          ) : null}

          <KycHeroCard statusInfo={statusInfo} />

          {kycStatus === "rejected" &&
          ((kycData as any)?.rejectReason ||
            (kycData as any)?.rejectedReason) ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
              <div className="font-bold">ເຫດຜົນທີ່ຖືກປະຕິເສດ</div>
              <div className="mt-1">
                {(kycData as any).rejectReason ||
                  (kycData as any).rejectedReason}
              </div>
            </div>
          ) : null}

          <KycFormCard
            activeDocument={activeDocument}
            initialValues={initialValues}
            isReady={isReady}
            isExistingKyc={Boolean((kycData as any)?._id)}
            onDocumentChange={setActiveDocumentType}
            onSubmit={handleKycSubmit}
          />
        </div>
      ) : (
        <KycApprovedCard
          fullName={
            (kycData as any)?.fullName || (authMeData as any)?.name || ""
          }
          username={(authMeData as any)?.username || ""}
          email={(authMeData as any)?.email || ""}
          phone={(authMeData as any)?.phone || ""}
          dateOfBirth={(kycData as any)?.dateOfBirth?.slice?.(0, 10) || ""}
          address={
            (kycData as any)?.address || (authMeData as any)?.address || ""
          }
          identityNo={(kycData as any)?.identityNo || ""}
          documentFrontImage={(kycData as any)?.documentFrontImage || ""}
          documentBackImage={(kycData as any)?.documentBackImage || ""}
          selfieWithDocumentImage={
            (kycData as any)?.selfieWithDocumentImage || ""
          }
          bankAccountName={(kycData as any)?.bankAccountName || ""}
          bankAccountNo={(kycData as any)?.bankAccountNo || ""}
          bankName={(kycData as any)?.bankName || ""}
        />
      )}
    </div>
  );
}
